import { useCallback, useEffect, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { Check, Link2, Loader2, Pencil, TriangleAlert, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { HelpTooltip } from '@/components/HelpTooltip'
import { apiFetch, apiErrorMessage } from '@/lib/csrf'
import { useBrand } from '@/hooks/useBrand'
import { formatLongDate } from '@/lib/account-dates'

/**
 * RENAMING THE BUSINESS — and, with it, the web address every one of its links carries.
 *
 * The whole point of this card is that the operator can SEE the consequence before accepting it.
 * Renaming used to be a bare text box; the address it produced was invisible until the URL bar
 * changed underneath them.
 *
 * THE ADDRESS IS NEVER COMPUTED HERE. Not once, not as a hint, not "just for the placeholder".
 * It is asked for (debounced) from the server, which resolves it with the very code that will save
 * it. A browser-side slugifier looks right until it isn't: Rails turns "O'Brien's Guesthouse" into
 * `o-brien-s-guesthouse` and "Hôtel Café Münster" into `hotel-cafe-munster`, and a hand-rolled
 * regex agrees with neither. Showing one address and saving another is worse than showing none.
 * Every string on screen below — the address, and the sentence explaining it — comes from the
 * server; this component chooses an icon and a colour and nothing else.
 */

const PREVIEW_DEBOUNCE_MS = 400

/** The server's verdict on a draft address (PreviewAccountAddressMutation). */
interface AddressPreview {
  /** What will be saved. null when the draft produces nothing usable at all. */
  address: string | null
  status: 'unchanged' | 'available' | 'adjusted' | 'taken' | 'invalid'
  /** Whether saving is allowed. The one field the Save button reads. */
  usable: boolean
  /** The one sentence shown under the field. Authored server-side. */
  message: string
}

interface BusinessIdentityCardProps {
  name: string
  /** The business's current web address (the segment in /a/…). */
  address: string
  settingsPath: string
  canManage: boolean
  memberCount: number
  propertyCount: number
  createdAt: string
  animationDelay?: number
}

/** Where the operator's links actually point. Read, not derived — it is literally where they are. */
function siteHost(): string {
  return typeof window === 'undefined' ? '' : window.location.host
}

const TONE: Record<AddressPreview['status'], string> = {
  unchanged: 'text-muted-foreground',
  available: 'text-emerald-600',
  adjusted: 'text-amber-600',
  taken: 'text-rose-600',
  invalid: 'text-rose-600',
}

function StatusIcon({ status }: { status: AddressPreview['status'] }) {
  if (status === 'taken' || status === 'invalid') return <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
  if (status === 'adjusted') return <Link2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
  return <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
}

export default function BusinessIdentityCard({
  name: savedName,
  address: savedAddress,
  settingsPath,
  canManage,
  memberCount,
  propertyCount,
  createdAt,
  animationDelay = 100,
}: BusinessIdentityCardProps) {
  const brand = useBrand()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(savedName)
  // null = "follow the name" (the default, and what anyone who never opens the address editor gets).
  // A string means the operator has taken the address over and it is used exactly as typed.
  const [customAddress, setCustomAddress] = useState<string | null>(null)
  const [preview, setPreview] = useState<AddressPreview | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const closeEditor = useCallback(() => {
    setEditing(false)
    setName(savedName)
    setCustomAddress(null)
    setPreview(null)
    setPreviewError(null)
  }, [savedName])

  // Ask the server what this draft produces. Debounced, and re-run on the first paint of the editor
  // so the address is on screen before a single key is pressed.
  useEffect(() => {
    if (!editing) return

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setPreviewing(true)
      apiFetch(`${settingsPath}/address-preview`, {
        method: 'POST',
        body: JSON.stringify({ name: name.trim(), address: customAddress ?? '' }),
      })
        .then((r) => r.json())
        .then((data: AddressPreview) => {
          setPreview(data)
          setPreviewError(null)
        })
        .catch((e) => setPreviewError(apiErrorMessage(e, 'We could not check that web address just now.')))
        .finally(() => setPreviewing(false))
    }, PREVIEW_DEBOUNCE_MS)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [editing, name, customAddress, settingsPath])

  const trimmedName = name.trim()
  const nothingToSave = trimmedName === savedName && (customAddress === null || preview?.status === 'unchanged')
  const blocked = !trimmedName || (preview !== null && !preview.usable)

  function save() {
    // The same three gates the Save button renders, so pressing Enter can never do something the
    // button refuses to — including saving a draft that changes nothing.
    if (blocked || saving || nothingToSave) return
    setSaving(true)
    router.patch(
      settingsPath,
      { name: trimmedName, address: customAddress ?? '' },
      {
        preserveScroll: true,
        // A successful rename lands on the business's NEW address, so there is no editor left to
        // close — the page it returns is a fresh one. Only the failure path comes back here.
        onFinish: () => setSaving(false),
        onSuccess: () => closeEditor(),
      }
    )
  }

  return (
    <section className="animate-slide-in" style={{ animationDelay: `${animationDelay}ms` }}>
      <div className="rounded-xl border border-border/60 bg-white shadow-sm">
        <div className="border-b border-border/40 px-5 py-4">
          <h2 className="text-sm font-semibold text-foreground">Business identity</h2>
          <p className="text-xs text-muted-foreground">
            The name your team sees across {brand}, and the web address your links use.
          </p>
        </div>

        <div className="p-5">
          {editing ? (
            <div className="space-y-5">
              {/* ── Name ─────────────────────────────────────────────────────────────────── */}
              <div className="space-y-2">
                <Label htmlFor="business-name">Business name</Label>
                <Input
                  id="business-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') save()
                    if (e.key === 'Escape') closeEditor()
                  }}
                  autoFocus
                  className="max-w-sm text-base font-semibold"
                  aria-label="Business name"
                />
              </div>

              {/* ── Web address ──────────────────────────────────────────────────────────── */}
              <div className="space-y-2">
                <div className="flex items-center">
                  <Label htmlFor="business-address">Web address</Label>
                  <HelpTooltip content="The part of your links that names your business. It normally follows your business name, but you can set it yourself." />
                </div>

                {customAddress === null ? (
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    {/* Normal text flow, not a flex row: a long address on a narrow screen then wraps
                        as text (left-aligned, `break-all`) instead of splitting into centred flex lines. */}
                    <span className="max-w-full break-all rounded-lg border border-border/60 bg-muted/50 px-3 py-2 font-mono text-sm text-foreground">
                      <span className="text-muted-foreground">{siteHost()}/a/</span>
                      {previewing && !preview ? (
                        <Loader2 className="ml-1 inline h-3.5 w-3.5 animate-spin align-text-bottom text-muted-foreground" />
                      ) : (
                        <span className="font-semibold">{preview?.address ?? savedAddress}</span>
                      )}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="rounded-lg text-xs"
                      onClick={() => setCustomAddress(preview?.address ?? savedAddress)}
                    >
                      <Pencil className="mr-1.5 h-3 w-3" />
                      Set it myself
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    {/* min-w-0 + a full-width inner field: on a narrow screen the address shrinks
                        inside the card instead of pushing the prefix out of it. */}
                    <div className="flex w-full min-w-0 max-w-sm items-center rounded-lg border border-border/60 bg-white pl-3 focus-within:ring-2 focus-within:ring-primary/30">
                      <span className="shrink-0 font-mono text-sm text-muted-foreground">{siteHost()}/a/</span>
                      <Input
                        id="business-address"
                        value={customAddress}
                        onChange={(e) => setCustomAddress(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') save()
                          if (e.key === 'Escape') closeEditor()
                        }}
                        aria-label="Web address"
                        // The field IS the container: no border, no background of its own and no ring
                        // (ring-offset-0 too — an offset alone re-inflates a zeroed ring), so the one
                        // focus outline is the container's and the prefix reads as part of the input.
                        className="w-full min-w-0 rounded-none border-0 bg-transparent px-1 font-mono text-sm font-semibold shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="rounded-lg text-xs"
                      onClick={() => setCustomAddress(null)}
                    >
                      <X className="mr-1.5 h-3 w-3" />
                      Follow the name instead
                    </Button>
                  </div>
                )}

                {/* One line, server-classified and server-worded — no comparison happens here. */}
                {previewError ? (
                  <p className="flex items-start gap-1.5 text-xs text-rose-600">
                    <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    {previewError}
                  </p>
                ) : preview ? (
                  <p className={`flex items-start gap-1.5 text-xs ${TONE[preview.status]}`}>
                    <StatusIcon status={preview.status} />
                    {preview.message}
                  </p>
                ) : null}

                <p className="text-xs text-muted-foreground">
                  Changing your web address is safe — links people already have will still bring them here.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <Button size="sm" className="rounded-lg" onClick={save} disabled={saving || blocked || nothingToSave}>
                  {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1.5 h-3.5 w-3.5" />}
                  Save changes
                </Button>
                <Button size="sm" variant="ghost" className="rounded-lg" onClick={closeEditor} disabled={saving}>
                  <X className="mr-1.5 h-3.5 w-3.5" />
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold tracking-tight text-foreground">{savedName}</span>
                {canManage && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 rounded-lg p-0"
                    onClick={() => setEditing(true)}
                    aria-label="Edit business name and web address"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              <p className="flex items-center gap-1.5 font-mono text-xs text-muted-foreground">
                <Link2 className="h-3 w-3 shrink-0" />
                {/* One flex child, so the gap above separates the icon from the address and nothing
                    splits the address itself. */}
                <span>
                  {siteHost()}/a/<span className="font-semibold text-foreground">{savedAddress}</span>
                </span>
              </p>
            </div>
          )}

          {/* The standing facts, ruled off from the editable identity above them. */}
          <p className="mt-4 border-t border-border/40 pt-3 text-xs text-muted-foreground">
            {memberCount} {memberCount === 1 ? 'member' : 'members'}
            <span className="mx-1.5 text-border">·</span>
            {propertyCount} {propertyCount === 1 ? 'property' : 'properties'}
            <span className="mx-1.5 text-border">·</span>
            Created {formatLongDate(createdAt)}
          </p>
        </div>
      </div>
    </section>
  )
}
